import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Undo2, AlertOctagon, Loader2, RefreshCw } from "lucide-react";
import { api } from "../../../lib/api";

export default function RecycleBinPanel() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterKind, setFilterKind] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const qs = new URLSearchParams({ limit: "500" });
            if (filterKind) qs.set("kind", filterKind);
            const r = await api.get(`/admin/master-ops/recycle/list?${qs.toString()}`);
            setRows(r.data.items || []);
        } finally { setLoading(false); }
    }, [filterKind]);

    useEffect(() => { load(); }, [load]);

    const restore = async (it) => {
        try {
            await api.post(`/admin/master/${it.kind}/${it.id}/restore`);
            toast.success(`Restored "${it.value}"`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to restore");
        }
    };

    const hardDelete = async (it) => {
        if (!window.confirm(`Permanently DELETE "${it.value}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/master-ops/recycle/${it.kind}/${it.id}`);
            toast.success("Purged permanently");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-200" data-testid="md-recycle-panel">
            <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
                <Trash2 className="w-4 h-4 text-rose-500" />
                <h3 className="font-heading text-sm font-bold text-[#0F172A]">Recycle Bin</h3>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 font-semibold">
                    Soft-deleted items live here. Restore anytime — or purge permanently.
                </span>
                <select
                    value={filterKind}
                    onChange={(e) => setFilterKind(e.target.value)}
                    data-testid="md-recycle-filter-kind"
                    className="ml-auto text-xs px-2 py-1.5 rounded border border-slate-200"
                >
                    <option value="">All kinds</option>
                    {["subject", "grade", "board", "country", "state", "city", "pincode",
                        "language", "exam_category", "curriculum_type", "curriculum_pack"].map((k) =>
                        <option key={k} value={k}>{k}</option>)}
                </select>
                <button onClick={load} data-testid="md-recycle-refresh" className="text-xs px-2 py-1.5 rounded border border-slate-200 inline-flex items-center gap-1 hover:bg-slate-50">
                    <RefreshCw className="w-3 h-3" /> Refresh
                </button>
            </div>
            <div className="max-h-[65vh] overflow-y-auto divide-y divide-slate-100">
                {loading && (
                    <div className="px-4 py-6 text-sm text-slate-500 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                    </div>
                )}
                {!loading && rows.length === 0 && (
                    <div className="px-4 py-8 text-sm text-slate-500 text-center flex flex-col items-center gap-2">
                        <AlertOctagon className="w-6 h-6 text-slate-300" />
                        Recycle bin is empty.
                    </div>
                )}
                {!loading && rows.map((it) => (
                    <div key={it.id} className="px-4 py-2.5 flex items-center gap-3 text-sm" data-testid={`md-recycle-row-${it.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 font-bold uppercase tracking-wider text-slate-600">
                                    {it.kind}
                                </span>
                                <span className="font-semibold text-slate-800">{it.value}</span>
                                {it.code && <span className="text-[10px] px-1 py-0.5 rounded bg-slate-50 border border-slate-200 text-slate-500">{it.code}</span>}
                            </div>
                            <div className="text-[10px] text-slate-400 mt-0.5">
                                Deleted by {it.deleted_by || "—"} · {it.deleted_at ? new Date(it.deleted_at).toLocaleString() : "—"}
                            </div>
                        </div>
                        <button
                            onClick={() => restore(it)}
                            data-testid={`md-recycle-restore-${it.id}`}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200 font-semibold"
                        >
                            <Undo2 className="w-3 h-3" /> Restore
                        </button>
                        <button
                            onClick={() => hardDelete(it)}
                            data-testid={`md-recycle-purge-${it.id}`}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-red-100 text-red-800 hover:bg-red-200 font-semibold"
                        >
                            <Trash2 className="w-3 h-3" /> Purge
                        </button>
                    </div>
                ))}
            </div>
            <div className="px-4 py-2 border-t border-slate-100 text-[11px] text-slate-500">{rows.length} items</div>
        </div>
    );
}
